import { useCallback, useRef, useState } from 'react'

import { ProbateEstatePerson } from 'lib/models/probate-estate'

import {
  PersonAttributes,
  useEstatePeople,
  useRoleCount,
} from './estate-people'
import { contactOptions } from './contact-select'
import { hasBothNames, ignoreFailure, NEW_PERSON } from './helpers'
import usePlaces from './use-places'
import { Role } from './roles'

/**
 * A card in a section: someone on the estate, or a draft who isn't saved
 * until both their names are filled in. Drafts have negative ids. A draft
 * can stand in for someone in the section, who keeps their place until the
 * draft is saved.
 */
export type SharedPersonRecord = ProbateEstatePerson & {
  isDraft?: true
  replaces?: number
  slot: number
}

type Draft = { id: number; replaces?: number }

const useSharedPeople = (role: Role) => {
  const { people, createPerson, deletePerson } = useEstatePeople()
  const [drafts, setDrafts] = useState<Draft[]>([])
  const lastDraftId = useRef(0)
  const { shownIds, slotFor, claimSlot, moveInto } = usePlaces(role.memberIds)

  const personWithId = (id: number) => people.find((person) => person.id === id)

  const toDraftRecord = ({ id, replaces }: Draft): SharedPersonRecord => ({
    id,
    type: 'probate_estate_people',
    attributes: {},
    isDraft: true,
    replaces,
    slot: replaces ? slotFor(replaces) : id,
  })

  const records: SharedPersonRecord[] = [
    // someone just saved can arrive in the section before the list of
    // people catches up
    ...shownIds.flatMap((id) => {
      const standIn = drafts.find((draft) => draft.replaces === id)
      if (standIn) return [toDraftRecord(standIn)]
      const person = personWithId(id)
      return person ? [{ ...person, slot: slotFor(id) }] : []
    }),
    ...drafts.filter((draft) => !draft.replaces).map(toDraftRecord),
  ]

  const createDraft = useCallback((replaces?: number) => {
    lastDraftId.current -= 1
    const id = lastDraftId.current
    setDrafts((current) => [...current, { id, replaces }])
  }, [])

  const addDraft = useCallback(() => createDraft(), [createDraft])

  const dropDraft = (id: number) =>
    setDrafts((current) => current.filter((draft) => draft.id !== id))

  // someone without both names isn't worth keeping as a contact
  const deleteIfUnnamed = (personId: number) => {
    const person = personWithId(personId)
    return person && !hasBothNames(person.attributes)
      ? deletePerson(personId)
      : undefined
  }

  const removePerson = async (personId: number) => {
    await role.remove(personId)
    await deleteIfUnnamed(personId)
  }

  const replacePerson = (fromId: number, toId: number) =>
    moveInto(slotFor(fromId), toId, {
      replacing: fromId,
      save: async () => {
        await role.replace(fromId, toId)
        await deleteIfUnnamed(fromId)
      },
    })

  const remove = async (id: number) => {
    const record = records.find((card) => card.id === id)
    if (!record) return
    if (!record.isDraft) return removePerson(id)
    if (record.replaces) await removePerson(record.replaces)
    dropDraft(id)
  }

  const choose = async (record: SharedPersonRecord, value: string | number) => {
    if (value === NEW_PERSON) {
      if (!record.isDraft) createDraft(record.id)
      return
    }

    const personId = Number(value)
    if (personId === record.id) return

    if (!record.isDraft) return replacePerson(record.id, personId)

    if (personId === record.replaces) return dropDraft(record.id)

    // they're moved in before the card's new person goes, so it's never
    // empty: React 16 doesn't batch these
    const movedIn = record.replaces
      ? replacePerson(record.replaces, personId)
      : moveInto(record.slot, personId, { save: () => role.add(personId) })
    dropDraft(record.id)
    return movedIn
  }

  const saveDraft = async (
    record: SharedPersonRecord,
    attributes: PersonAttributes
  ) => {
    // kept until they're saved, so a failed save doesn't lose what was typed
    if (record.replaces) {
      const person = await createPerson(attributes)
      const movedIn = replacePerson(record.replaces, person.id)
      dropDraft(record.id)
      await movedIn
      return person.id
    }

    const personId = await role.addNew(attributes)
    claimSlot(personId, record.slot)
    dropDraft(record.id)
    return personId
  }

  const roleCount = useRoleCount()
  const hasSharedContacts = records.some(
    (record) => !record.isDraft && roleCount(record) > 1
  )

  const cardProps = (record: SharedPersonRecord) => ({
    record,
    options: contactOptions({
      people,
      memberIds: role.memberIds,
      currentId: record.isDraft ? record.replaces : record.id,
      isDraft: !!record.isDraft,
    }),
    onChoose: (value: string | number) => ignoreFailure(choose(record, value)),
    onSaveDraft: (attributes: PersonAttributes) =>
      saveDraft(record, attributes),
  })

  return {
    records,
    hasSharedContacts,
    people,
    addDraft,
    remove,
    cardProps,
  }
}

export default useSharedPeople
